// ./src/utils/api/ServiceIntervalAccountService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { ExpenseKind } from "./AppEssentialsService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const SERVICE_INTERVAL_ACCOUNT_FIELDS = `
  carId
  kindId
  intervalType
  mileageInterval
  daysInterval
  isCustomized
  expenseKind {
    id
    code
    name
    canSchedule
    isItMaintenance
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const SERVICE_INTERVAL_ACCOUNT_LIST_QUERY = gql`
  query ServiceIntervalAccountList(
    $filter: ServiceIntervalAccountFilter!
    $params: PaginationAndSorting
  ) {
    serviceIntervalAccountList(filter: $filter, params: $params) {
      code
      data {
        ${SERVICE_INTERVAL_ACCOUNT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const SERVICE_INTERVAL_ACCOUNT_SET_MUTATION = gql`
  mutation ServiceIntervalAccountSet($params: ServiceIntervalAccountInput!) {
    serviceIntervalAccountSet(params: $params) {
      code
      data {
        ${SERVICE_INTERVAL_ACCOUNT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SERVICE_INTERVAL_ACCOUNT_REMOVE_MUTATION = gql`
  mutation ServiceIntervalAccountRemove(
    $where: ServiceIntervalAccountWhereInput!
  ) {
    serviceIntervalAccountRemove(where: $where) {
      code
      data {
        ${SERVICE_INTERVAL_ACCOUNT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SERVICE_INTERVAL_ACCOUNT_REMOVE_MANY_MUTATION = gql`
  mutation ServiceIntervalAccountRemoveMany(
    $where: [ServiceIntervalAccountWhereInput!]!
  ) {
    serviceIntervalAccountRemoveMany(where: $where) {
      code
      data {
        ${SERVICE_INTERVAL_ACCOUNT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface ServiceIntervalAccount {
  carId?: string | undefined;
  kindId?: number | undefined;
  intervalType?: number | undefined;
  mileageInterval?: number | undefined;
  daysInterval?: number | undefined;
  isCustomized?: boolean | undefined;
  expenseKind?: ExpenseKind | undefined;
}

export interface ServiceIntervalAccountInput {
  carId: string;
  kindId: number;
  intervalType: number;
  mileageInterval?: number | null | undefined;
  daysInterval?: number | null | undefined;
}

export interface ServiceIntervalAccountFilter {
  carId: string;
  kindId?: number[] | null | undefined;
  intervalType?: number[] | null | undefined;
}

export interface ServiceIntervalAccountWhereInput {
  carId: string;
  kindId: number;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * ServiceIntervalAccount service that handles maintenance interval settings per car.
 * Used to configure custom service intervals (mileage-based or time-based) for specific maintenance types.
 *
 * The service returns a merged view of default intervals and car-specific customizations.
 * - `list`: Returns all intervals for a car (defaults merged with customizations)
 * - `set`: Creates or updates a car-specific customization
 * - `remove`: Removes a customization, reverting to the default interval
 */
export class ServiceIntervalAccountService {
  /**
   * Get a list of service intervals for a specific car.
   * Returns merged view of defaults and car-specific customizations.
   * The `isCustomized` field indicates whether the interval has been customized.
   */
  static async list(
    filter: ServiceIntervalAccountFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SERVICE_INTERVAL_ACCOUNT_LIST_QUERY,
      {
        filter,
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Set (create or update) a car-specific service interval customization.
   * Returns the saved interval with `isCustomized: true`.
   */
  static async set(params: ServiceIntervalAccountInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SERVICE_INTERVAL_ACCOUNT_SET_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Remove a car-specific customization, reverting to the default interval.
   * Returns the default interval with `isCustomized: false`.
   */
  static async remove(carId: string, kindId: number): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SERVICE_INTERVAL_ACCOUNT_REMOVE_MUTATION,
      {
        where: { carId, kindId },
      }
    );
    return result;
  }

  /**
   * Remove multiple car-specific customizations, reverting them to defaults.
   * Returns the default intervals with `isCustomized: false`.
   */
  static async removeMany(
    items: ServiceIntervalAccountWhereInput[]
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SERVICE_INTERVAL_ACCOUNT_REMOVE_MANY_MUTATION,
      {
        where: items,
      }
    );
    return result;
  }
}